import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { IconArrowDown } from '@icons';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Points, textSize } from '@styles';
import { useTranslations } from '@libs/translations';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container, PageTop } from '@components/Help-Center-Components';
import { HELP_CENTER } from '@constants';

export const HotkeysSection = ({ data }) => {
  const translations = useTranslations();
  const [activeCategory, setActiveCategory] = useState(data.categories[0]?.slug);
  const [isMobile, setIsMobile] = useState(true);
  const [expandedStates, setExpandedStates] = useState({});
  const manualScrollRef = useRef(false);

  const refs = useRef({});
  const contentRefs = useRef({});

  data.categories.forEach((s) => {
    if (!refs.current[s.slug]) {
      refs.current[s.slug] = React.createRef();
    }
  });

  const scrollToCategory = (slug) => {
    const ref = refs.current[slug];
    const offset = 102;
    const y = ref.current.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveCategory(slug); // критично для мгновенной подсветки
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCategory = (slug) => {
    setExpandedStates((prev) => ({
      ...prev,
      [slug]: !prev[slug],
    }));
  };

  useEffect(() => {
    const initStates = {};
    data.categories.forEach((cat, idx) => {
      initStates[cat.slug] = isMobile ? idx === 0 : true;
      if (!refs.current[cat.slug]) refs.current[cat.slug] = React.createRef();
      if (!contentRefs.current[cat.slug]) contentRefs.current[cat.slug] = React.createRef();
    });
    setExpandedStates(initStates);
  }, [data.categories, isMobile]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slug = entry.target.getAttribute('data-category');
          if (entry.isIntersecting) {
            setActiveCategory(slug);
            break;
          }
        }
      },
      {
        root: null,
        rootMargin: '0px 0px -60% 0px',
        threshold: 0.3,
      }
    );

    data.categories.forEach((s) => {
      const ref = refs.current[s.slug];
      if (ref?.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [data.categories]);

  const links = [
    {
      href: `/${HELP_CENTER.MAIN}`,
      name: translations.t('site.help.title'),
    },
    {
      href: '',
      name: `${data.caption}`,
    },
  ];

  return (
    <Wrapper>
      <Breadcrumbs links={links} />
      <Container>
        <PageTop title={data.caption} description={data.description} />
        <ContentWrapper>
          <MainContent>
            {data.categories.map((category, idx) => {
              const isOpen = expandedStates[category.slug];
              return (
                <Category key={category.slug} ref={refs.current[category.slug]} data-category={category.slug}>
                  <CategoryHeader onClick={() => toggleCategory(category.slug)}>
                    <CategoryTitle>{category.caption}</CategoryTitle>
                    <IconWrapper isOpen={isOpen}>
                      <IconArrowDown />
                    </IconWrapper>
                  </CategoryHeader>
                  <CollapsibleContent ref={contentRefs.current[category.slug]} isOpen={isOpen} isFirst={idx === 0}>
                    <KeysList>
                      {data.items
                        .filter((item) => item.category === category.slug)
                        .map((item) => (
                          <KeyRow key={`${item.slug}-${item.id}`}>
                            {item.slug.length > 0 ? (
                              <Link
                                href={`/${HELP_CENTER.MAIN}/${HELP_CENTER.KEYBOARD_SHORTCUTS}/${item.category}/${item.slug}`}
                                passHref>
                                <KeyRowLink>
                                  <KeyCaption>{item.description}</KeyCaption>
                                  <KeyDescription>{item.caption}</KeyDescription>
                                </KeyRowLink>
                              </Link>
                            ) : (
                              <>
                                <KeyCaption>{item.description}</KeyCaption>
                                <KeyDescription>{item.caption}</KeyDescription>
                              </>
                            )}
                          </KeyRow>
                        ))}
                    </KeysList>
                  </CollapsibleContent>
                </Category>
              );
            })}
          </MainContent>

          <AsideContent>
            <AsideTitle>{translations.t('site.help.articleHotkey')}</AsideTitle>
            <Navigation>
              {data.categories.map((item) => (
                <NavLink
                  key={item.slug}
                  onClick={() => scrollToCategory(item.slug)}
                  isActive={activeCategory === item.slug}
                  aria-label={item.caption}>
                  {item.caption}
                </NavLink>
              ))}
            </Navigation>
          </AsideContent>
        </ContentWrapper>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin: 55px 16px 0;
  max-width: 1386px;

  @media ${Points.tablet_landscape} {
    margin: 30px 28px 0;
  }

  @media ${Points.laptop_wide} {
    margin: 40px auto 0;
    padding: 0 53px;
  }

  @media ${Points.fullhd} {
    padding: 0;
  }

  html {
    scroll-behavior: smooth;
  }
`;

const ContentWrapper = styled.div`
  margin-top: 60px;

  @media ${Points.tablet_landscape} {
    display: flex;
    position: relative;
    justify-content: space-between;
    margin-top: 70px;
  }

  @media ${Points.laptop_wide} {
    margin-top: 80px;
  }

  @media ${Points.fullhd} {
    margin-top: 90px;
  }
`;

const MainContent = styled.div`
  @media ${Points.tablet_landscape} {
    width: 600px;
  }

  @media ${Points.laptop_wide} {
    width: 640px;
  }

  @media ${Points.fullhd} {
    width: 690px;
  }
`;

const AsideContent = styled.aside`
  display: none;
  @media ${Points.tablet_landscape} {
    display: block;
    position: sticky;
    top: 0;
    align-self:
    top: 0;
    z-index: 1;
    width: 200px;
    padding-top: 40px;
  }

  @media ${Points.laptop_wide} {
    width: 240px;
    height: fit-content;
  }

  @media ${Points.fullhd} {
    padding-top: 70px;
    width: 266px;
  }
`;

const AsideTitle = styled.h2`
  ${textSize('body')};
  font-weight: 400;
  color: ${Colors.main_dark};
  margin-bottom: 20px;

  @media ${Points.fullhd} {
    margin-bottom: 25px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 15px;
  color: ${Colors.dark_500};
`;

const NavLink = styled.a`
  ${Mixins.resetLink()};
  ${textSize('description')};
  color: ${(p) => (p.isActive ? Colors.main_blue : Colors.dark_500)};
  cursor: pointer;
  &:hover {
    color: ${(p) => !p.isActive && Colors.second_blue};
  }

  &:active {
    color: ${(p) => !p.isActive && Colors.main_blue};
  }
`;

const Category = styled.div``;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 20px 0;
  border-bottom: 1px solid #e1e1e1;
  cursor: pointer;
  align-items: baseline;

  @media ${Points.fullhd} {
    padding: 25px 0;
  }

  details > summary::-webkit-details-marker {
    display: none;
  }

  span {
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: end;
  }
`;

const CategoryTitle = styled.h2`
  ${textSize('large_card_header')};
  font-weight: 400;
`;

const IconWrapper = styled.div`
  transition: transform 0.4s ease;
  transform: rotate(${(p) => (p.isOpen ? '180deg' : '0deg')});
`;

const CollapsibleContent = styled.div`
  overflow: hidden;
  transition: max-height 0.4s ease, opacity 0.4s ease;
  max-height: ${(p) => (p.isOpen ? 'auto' : '0')};
  opacity: ${(p) => (p.isOpen ? 1 : 0)};
`;

const KeysList = styled.ul`
  list-style: none;
  ${Mixins.resetList()};
  display: flex;
  flex-direction: column;

  @media ${Points.tablet_landscape} {
    margin-bottom: 0;
  }
`;

const KeyRow = styled.li`
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid ${Colors.dark_200};

  @media ${Points.fullhd} {
    padding: 20px 0;
    gap: 20px;
  }

  &:has(a)::after {
    position: absolute;
    content: '';
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='13' height='10' viewBox='0 0 13 10' fill='none'%3e%3cpath d='M0.666016 5L11.9994 5M11.9994 5L8.2183 1M11.9994 5L8.2183 9' stroke='%23292929' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    width: 16px;
    height: 16px;
    right: 0;
    top: 20px;

    @media ${Points.tablet_landscape} {
      display: none;
    }
  }

  &:has(a):active {
    border-color: ${Colors.blue_500};
    color: ${Colors.main_dark};
  }

  @media (hover: hover) and (pointer: fine) {
    &:has(a):hover {
      border-color: ${Colors.blue_500};
      color: ${Colors.main_dark};
    }

    &:has(a):hover::after {
      display: block;
      position: absolute;
      content: '';
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='13' height='10' viewBox='0 0 13 10' fill='none'%3e%3cpath d='M0.666016 5L11.9994 5M11.9994 5L8.2183 1M11.9994 5L8.2183 9' stroke='%23292929' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      width: 16px;
      height: 16px;
      right: 0;
      top: 20px;

      @media ${Points.fullhd} {
        top: 27px;
      }
    }
  }
`;

const KeyRowLink = styled.a`
  ${Mixins.resetLink()};
  display: flex;
  flex-wrap: nowrap;
  gap: 15px;
  width: inherit;
  position: relative;
  width: 100%;
  color: ${Colors.main_grey};
`;

const KeyCaption = styled.div`
  width: 125px;
  min-width: 125px;
  text-wrap-mode: wrap;
  white-space: break-spaces;
  word-break: break-word;

  @media ${Points.tablet_landscape} {
    width: 220px;
    min-width: 220px;
  }

  @media ${Points.fullhd} {
    width: 240px;
    min-width: 240px;
  }
`;

const KeyDescription = styled.div`
  flex: 1;
  padding-right: 31px;
`;
